import React, { useState, useMemo } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { themeColors } from '../theme';
import PlotExportBar from './PlotExportBar';
import { exportCSV } from '../utils/export';
import { mppDot } from './MppMarker';
import ChartCrosshair from './ChartCrosshair';
import { findZeroCrossing, jvXDomain, jvYDomain } from '../utils/chartDomain';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1`;

// ── JV data extractors ─────────────────────────────────────────────────────
// Every saved curve must render ALL its points through its own zero
// crossing (the Voc intercept). The old implementation truncated at
// metrics.Voc + 0.02 V — whenever metrics.Voc was missing (fallback 1.0)
// or stale, curves ended short of the x-axis crossover while the main
// window showed the same curve fully. The crossing is now found from the
// curve data itself (findZeroCrossing) and points are kept through the
// crossing plus a small margin; the |J| < 500 filter still drops the
// solver's forward-bias blow-up points far past Voc.
const getJV = (sim) => {
  const pts = (sim?.results?.jv_curve || [])
    .filter(pt => Number.isFinite(pt.V) && Number.isFinite(pt.J)
                  && Math.abs(pt.J) < 500)
    .map(pt => ({ V: Number(pt.V), J: Number(pt.J) }))
    .sort((a, b) => a.V - b.V);
  if (!pts.length) return pts;
  const cross = findZeroCrossing(pts);
  if (cross == null) return pts;
  const span = pts[pts.length - 1].V - pts[0].V;
  const vCut = cross + Math.max(0.03, 0.05 * span);
  return pts.filter(p => p.V <= vCut);
};

// |Jsc| used by the Normalized toggle, with a (0, 100) mA/cm² sanity clamp.
const getJscFromSim = (sim) => {
  const m = Math.abs(Number(sim?.metrics?.Jsc || 0));
  return (m > 0 && m < 100) ? m : 25;
};

const PROFILE_FIELDS = [
  { key: 'G',       label: 'Generation G(x)',       unit: 'cm⁻³s⁻¹', log: true  },
  { key: 'R',       label: 'Recombination R(x)',    unit: 'cm⁻³s⁻¹', log: true  },
  { key: 'n',       label: 'Electron density n(x)', unit: 'cm⁻³',    log: true  },
  { key: 'p',       label: 'Hole density p(x)',     unit: 'cm⁻³',    log: true  },
  { key: 'E_field', label: 'Electric field E(x)',   unit: 'V/cm',    log: false },
];

export default function ComparisonModal({ theme = 'dark', savedSims = [], selectedSims = [], onClose }) {
  const c = themeColors(theme);
  const sims = useMemo(
    () => savedSims.filter(s => selectedSims.includes(s.id)),
    [savedSims, selectedSims]);

  const [tab, setTab] = useState('jv');
  const [normalized, setNormalized] = useState(false);
  const [profileKey, setProfileKey] = useState('G');

  // EQE-on-demand state
  const [eqeSpectra, setEqeSpectra] = useState({}); // {simId: [{wavelength_nm, eqe}]}
  const [eqeRunning, setEqeRunning] = useState(false);
  const [eqeProgress, setEqeProgress] = useState({ done: 0, total: 0, current: '' });
  const [eqeError, setEqeError] = useState(null);

  if (!sims.length) {
    return (
      <Overlay onClose={onClose} c={c}>
        <div style={{ padding: 40, color: c.textMuted, textAlign: 'center' }}>
          Select 2 or more saved simulations to compare.
        </div>
      </Overlay>
    );
  }

  const tabs = [
    { id: 'jv',       label: 'JV Curves' },
    { id: 'bands',    label: 'Band Diagrams' },
    { id: 'profiles', label: 'Profiles' },
    { id: 'eqe',      label: 'EQE' },
  ];

  return (
    <Overlay onClose={onClose} c={c}>
      <div style={{
        display: 'flex', flexDirection: 'column',
        height: '100%', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 20px', borderBottom: `1px solid ${c.border}`,
          background: c.bgSecondary,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h2 style={{
              margin: 0, fontSize: 16, color: c.text,
              fontFamily: 'IBM Plex Mono, monospace', letterSpacing: 1,
            }}>COMPARISON VIEW ({sims.length} sims)</h2>
            <Toggle
              left="Absolute" right="Normalized"
              right_active={normalized}
              onChange={() => setNormalized(v => !v)}
              c={c}
            />
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: `1px solid ${c.border}`,
            color: c.text, padding: '4px 12px', borderRadius: 4,
            cursor: 'pointer', fontFamily: 'IBM Plex Mono, monospace',
          }}>Close ×</button>
        </div>

        {/* Summary table */}
        <SummaryTable sims={sims} c={c} />

        {/* Tab bar */}
        <div style={{
          display: 'flex', gap: 4, padding: '8px 20px 0',
          borderBottom: `1px solid ${c.border}`,
        }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{
                padding: '6px 14px', cursor: 'pointer',
                background: tab === t.id ? c.bg : 'transparent',
                color: tab === t.id ? c.text : c.textMuted,
                border: `1px solid ${tab === t.id ? c.border : 'transparent'}`,
                borderBottom: 'none',
                borderRadius: '4px 4px 0 0',
                fontFamily: 'IBM Plex Mono, monospace', fontSize: 13,
              }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Content — minWidth:0 keeps flex children (charts) from
            collapsing to width:-1 when the modal narrows. */}
        <div style={{
          flex: 1, overflow: 'auto', background: c.bg, padding: 20,
          minWidth: 0,
        }}>
          {tab === 'jv' && <JVCompare sims={sims} normalized={normalized} c={c} />}
          {tab === 'bands' && <BandsCompare sims={sims} c={c} />}
          {tab === 'profiles' && (
            <ProfilesCompare
              sims={sims}
              profileKey={profileKey}
              setProfileKey={setProfileKey}
              normalized={normalized}
              c={c}
            />
          )}
          {tab === 'eqe' && (
            <EQECompare
              sims={sims}
              eqeSpectra={eqeSpectra}
              setEqeSpectra={setEqeSpectra}
              eqeRunning={eqeRunning}
              setEqeRunning={setEqeRunning}
              eqeProgress={eqeProgress}
              setEqeProgress={setEqeProgress}
              eqeError={eqeError}
              setEqeError={setEqeError}
              normalized={normalized}
              c={c}
            />
          )}
        </div>
      </div>
    </Overlay>
  );
}

function Overlay({ children, onClose, c }) {
  // --chat-width is the CSS custom property PersistentAI keeps updated
  // with its current fixed-panel width (0 when collapsed) — the same
  // variable the workspace uses for its padding-right. Subtracting it
  // here keeps the comparison window fully visible next to the AI chat
  // instead of sliding underneath it.
  return (
    <div onClick={onClose} style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.65)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      paddingRight: 'var(--chat-width, 0px)',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: 'calc(100vw - var(--chat-width, 0px) - 48px)',
        height: '90vh', maxWidth: 1280, minWidth: 320,
        background: c.bg, border: `1px solid ${c.border}`,
        borderRadius: 8, overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
      }}>
        {children}
      </div>
    </div>
  );
}

function Toggle({ left, right, right_active, onChange, c }) {
  return (
    <div onClick={onChange} style={{
      display: 'inline-flex', cursor: 'pointer',
      border: `1px solid ${c.border}`, borderRadius: 4,
      fontFamily: 'IBM Plex Mono, monospace', fontSize: 11,
      overflow: 'hidden',
    }}>
      <span style={{
        padding: '3px 10px',
        background: !right_active ? c.accent : 'transparent',
        color: !right_active ? c.btnPrimaryText : c.textMuted,
      }}>{left}</span>
      <span style={{
        padding: '3px 10px',
        background: right_active ? c.accent : 'transparent',
        color: right_active ? c.btnPrimaryText : c.textMuted,
      }}>{right}</span>
    </div>
  );
}

// ── Summary table ──────────────────────────────────────────────────────────
function SummaryTable({ sims, c }) {
  const fmt = (v, d = 3) => v != null && Number.isFinite(Number(v))
    ? Number(v).toFixed(d) : '—';
  const cols = [
    { key: 'Voc', label: 'Voc (V)',     d: 4, abs: false },
    { key: 'Jsc', label: 'Jsc (mA/cm²)',d: 2, abs: true  },
    { key: 'FF',  label: 'FF (%)',      d: 1, abs: false },
    { key: 'PCE', label: 'PCE (%)',     d: 2, abs: false },
  ];
  const valOf = (s, col) => {
    const raw = s.metrics?.[col.key];
    if (raw == null || !Number.isFinite(Number(raw))) return null;
    return col.abs ? Math.abs(Number(raw)) : Number(raw);
  };

  // Best-minus-worst delta per column (skip if any sim missing the value)
  const deltas = cols.map(col => {
    const vals = sims.map(s => valOf(s, col)).filter(v => v != null);
    if (vals.length < 2) return null;
    return Math.max(...vals) - Math.min(...vals);
  });

  const cell = {
    padding: '6px 10px',
    fontFamily: 'IBM Plex Mono, monospace', fontSize: 12,
    borderBottom: `1px solid ${c.border}`,
    color: c.text,
  };
  const headerCell = { ...cell, color: c.textMuted, fontSize: 11,
                        textTransform: 'uppercase', letterSpacing: '1px' };

  return (
    <div style={{
      padding: '8px 20px', borderBottom: `1px solid ${c.border}`,
      background: c.bgSecondary,
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: `minmax(180px, 1.5fr) repeat(${cols.length}, 1fr)`,
        border: `1px solid ${c.border}`, borderRadius: 4,
        overflow: 'hidden', background: c.bg,
      }}>
        <div style={headerCell}>Simulation</div>
        {cols.map(col => (
          <div key={col.key} style={{ ...headerCell, textAlign: 'right' }}>{col.label}</div>
        ))}
        {sims.map(s => (
          <React.Fragment key={s.id}>
            <div style={{
              ...cell, display: 'flex', alignItems: 'center', gap: 8,
              minWidth: 0, overflow: 'hidden',
            }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%',
                background: s.color, flexShrink: 0,
              }} />
              <span style={{
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{s.name}</span>
            </div>
            {cols.map(col => {
              const v = valOf(s, col);
              return (
                <div key={col.key} style={{ ...cell, textAlign: 'right' }}>
                  {fmt(v, col.d)}
                </div>
              );
            })}
          </React.Fragment>
        ))}
        {/* Δ row */}
        <div style={{ ...cell, fontStyle: 'italic', color: c.textMuted }}>
          Δ (max − min)
        </div>
        {cols.map((col, i) => (
          <div key={col.key} style={{ ...cell, textAlign: 'right',
            color: c.textMuted, fontStyle: 'italic' }}>
            {deltas[i] != null ? fmt(deltas[i], col.d) : '—'}
          </div>
        ))}
      </div>
      <div style={{
        display: 'flex', justifyContent: 'flex-end',
        marginTop: 6,
      }}>
        <button
          onClick={() => {
            const rows = sims.map(s => {
              const r = { simulation: s.name };
              cols.forEach(col => { r[col.label] = valOf(s, col); });
              return r;
            });
            // Append delta row
            const delta = { simulation: 'delta_max_minus_min' };
            cols.forEach((col, i) => { delta[col.label] = deltas[i]; });
            rows.push(delta);
            exportCSV(rows, 'comparison_summary');
          }}
          style={{
            padding: '4px 10px', fontSize: 11, cursor: 'pointer',
            background: c.btnSecondary, color: c.btnSecondaryText,
            border: `1px solid ${c.border}`, borderRadius: 4,
            fontFamily: 'IBM Plex Mono, monospace',
          }}>
          📊 Export Summary
        </button>
      </div>
    </div>
  );
}

// ── JV ──────────────────────────────────────────────────────────────────────
function JVCompare({ sims, normalized, c }) {
  // Per-sim point arrays (already trimmed through each curve's zero
  // crossing by getJV, normalized when the toggle is on). Used for the
  // wide-format chart data, the axis domains, and the crosshair readout.
  const seriesPts = useMemo(() => sims.map(s => {
    const Jsc = Math.max(getJscFromSim(s), 1e-12);
    return getJV(s).map(p => ({ V: p.V, J: normalized ? p.J / Jsc : p.J }));
  }), [sims, normalized]);

  // Wide-format dataset with one column per sim, keyed by V.
  const data = useMemo(() => {
    const allV = new Set();
    seriesPts.forEach(pts => pts.forEach(p => allV.add(p.V)));
    const sortedV = Array.from(allV).sort((a, b) => a - b);
    const lookup = seriesPts.map(pts => new Map(pts.map(p => [p.V, p.J])));
    return sortedV.map(v => {
      const row = { V: v };
      sims.forEach((s, i) => {
        const val = lookup[i].get(v);
        if (val != null && Number.isFinite(val)) row[s.name] = val;
      });
      return row;
    });
  }, [sims, seriesPts]);

  // Manual overrides for V and J ranges. null = auto (data-driven).
  const [compXMin, setCompXMin] = useState(null);
  const [compXMax, setCompXMax] = useState(null);
  const [compYMin, setCompYMin] = useState(null);
  const [compYMax, setCompYMax] = useState(null);

  // ── Data-driven axis domains ─────────────────────────────────────────
  // X spans the union of all curves and always extends past the latest
  // zero crossing so every Voc intercept renders; Y spans the J extents
  // inside the X window with ~5% padding (blow-up capped at |Jsc| by the
  // helper). No hardcoded [0, 1.2]-style windows.
  const autoX = useMemo(() => jvXDomain(seriesPts), [seriesPts]);
  const xLo = compXMin != null ? compXMin : (autoX ? autoX[0] : 0);
  const xHi = compXMax != null ? compXMax : (autoX ? autoX[1] : 1.2);
  const autoY = useMemo(
    () => jvYDomain(seriesPts, xLo, xHi), [seriesPts, xLo, xHi]);
  const yFallback = normalized ? 1.2 : 30;
  const yLo = compYMin != null ? compYMin : (autoY ? autoY[0] : -yFallback);
  const yHi = compYMax != null ? compYMax : (autoY ? autoY[1] :  yFallback);

  // Continuous crosshair readout — mouse x-pixel inside the chart div.
  const [hoverX, setHoverX] = useState(null);
  const hoverSeries = useMemo(() => sims.map((s, i) => ({
    name: s.name, color: s.color,
    points: seriesPts[i].map(p => ({ x: p.V, y: p.J })),
  })).filter(s => s.points.length > 1), [sims, seriesPts]);

  const inp = {
    width: 56, padding: '3px 6px', fontSize: 11,
    background: c.input, color: c.inputText,
    border: `1px solid ${c.border}`, borderRadius: 3,
    fontFamily: 'IBM Plex Mono, monospace',
  };
  const reset = () => {
    setCompXMin(null); setCompXMax(null);
    setCompYMin(null); setCompYMax(null);
  };
  const numOrNull = v => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };

  // CSV builder: one row per V, one column per sim
  const csvRows = useMemo(() =>
    data.map(row => {
      const out = { 'V(V)': row.V };
      sims.forEach(s => {
        out[`${s.name}(mA/cm2)`] = row[s.name] != null ? row[s.name] : '';
      });
      return out;
    }), [data, sims]);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Axis controls */}
      <div style={{
        display: 'flex', gap: 6, alignItems: 'center',
        padding: '4px 0 8px', fontSize: 11, color: c.textMuted,
        fontFamily: 'IBM Plex Mono, monospace', flexWrap: 'wrap',
      }}>
        <span>V:</span>
        <input type="number" step={0.1} placeholder="auto"
          value={compXMin != null ? compXMin : ''}
          onChange={e => setCompXMin(numOrNull(e.target.value))}
          style={inp} />
        <span>→</span>
        <input type="number" step={0.1} placeholder="auto"
          value={compXMax != null ? compXMax : ''}
          onChange={e => setCompXMax(numOrNull(e.target.value))}
          style={inp} />
        <span style={{ marginLeft: 10 }}>J:</span>
        <input type="number" step={1} placeholder="auto"
          value={compYMin != null ? compYMin : ''}
          onChange={e => setCompYMin(numOrNull(e.target.value))}
          style={inp} />
        <span>→</span>
        <input type="number" step={1} placeholder="auto"
          value={compYMax != null ? compYMax : ''}
          onChange={e => setCompYMax(numOrNull(e.target.value))}
          style={inp} />
        <button onClick={reset} style={{
          padding: '3px 10px', fontSize: 11, cursor: 'pointer',
          background: c.btnSecondary, color: c.btnSecondaryText,
          border: `1px solid ${c.border}`, borderRadius: 3,
          fontFamily: 'IBM Plex Mono, monospace',
        }}>Reset</button>
      </div>

      <div id="comparison-jv-chart" style={{
        width: '100%', height: 380, minHeight: 380, minWidth: 200,
        position: 'relative',
      }}
        onMouseMove={e => {
          const r = e.currentTarget.getBoundingClientRect();
          setHoverX(e.clientX - r.left);
        }}
        onMouseLeave={() => setHoverX(null)}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 30, bottom: 40, left: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={c.plotGrid} />
            <XAxis dataKey="V" type="number" domain={[xLo, xHi]}
              tickFormatter={v => Number(v).toFixed(2)}
              tick={{ fill: c.plotText, fontSize: 12, fontFamily: 'IBM Plex Mono' }}
              label={{ value: 'Voltage (V)', position: 'insideBottom', offset: -10,
                fill: c.plotText, fontSize: 13, fontFamily: 'IBM Plex Mono' }} />
            <YAxis domain={[yLo, yHi]} allowDataOverflow
              tickFormatter={v => normalized ? Number(v).toFixed(2) : Number(v).toFixed(1)}
              tick={{ fill: c.plotText, fontSize: 12, fontFamily: 'IBM Plex Mono' }}
              label={{ value: normalized ? 'J / |Jsc|' : 'J (mA/cm²)',
                angle: -90, position: 'insideLeft', offset: -4,
                textAnchor: 'middle', fill: c.plotText, fontSize: 13,
                fontFamily: 'IBM Plex Mono' }} />
            <ReferenceLine y={0} stroke={c.textMuted} strokeDasharray="4 4" />
            {sims.map(s => (
              <Line key={s.id} type="monotone" dataKey={s.name}
                stroke={s.color} strokeWidth={2} dot={false}
                connectNulls isAnimationActive={false} />
            ))}
            {/* MPP marker per sim, same color as its curve (MppMarker.js) */}
            {sims.map(s => mppDot({
              metrics: s.metrics, jvCurve: getJV(s), color: s.color,
              yDivisor: normalized ? Math.max(getJscFromSim(s), 1e-12) : null,
              key: `mpp-${s.id}`,
            }))}
            {/* Continuous crosshair — interpolated (V, J) readout for
                every saved curve, replaces the nearest-point Tooltip. */}
            <ChartCrosshair hoverX={hoverX} series={hoverSeries}
              xLabel="V" xUnit="V" yUnit={normalized ? '' : 'mA/cm²'} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <PlotExportBar
        chartId="comparison-jv-chart"
        csvData={csvRows}
        csvFilename="comparison_jv"
        plotFilename="comparison_jv"
        c={c}
        disabled={data.length === 0}
      />
    </div>
  );
}

// ── Band Diagrams ──────────────────────────────────────────────────────────
function BandsCompare({ sims, c }) {
  // Combined CSV: one row per x_nm with Ec/Ev for each sim.  Each sim has
  // its own x grid, so we union across sims and lookup per x.
  const csvRows = useMemo(() => {
    const xs = new Set();
    sims.forEach(s => (s.results?.band_diagram || []).forEach(p => xs.add(p.x_nm)));
    const sortedX = Array.from(xs).sort((a, b) => a - b);
    const lookup = sims.map(s => {
      const m = new Map();
      (s.results?.band_diagram || []).forEach(p => m.set(p.x_nm, p));
      return m;
    });
    return sortedX.map(x => {
      const row = { 'position(nm)': x };
      sims.forEach((s, i) => {
        const p = lookup[i].get(x);
        row[`${s.name}_Ec(eV)`] = p ? p.Ec : '';
        row[`${s.name}_Ev(eV)`] = p ? p.Ev : '';
        row[`${s.name}_EFn(eV)`] = p ? p.Efn : '';
        row[`${s.name}_EFp(eV)`] = p ? p.Efp : '';
      });
      return row;
    });
  }, [sims]);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
    <div id="comparison-bands-chart" style={{
      flex: 1, minHeight: 0,
      display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
      gap: 16, overflowY: 'auto',
    }}>
      {sims.map(s => {
        const bd = (s.results?.band_diagram || []).map(p => ({
          x: p.x_nm, Ec: p.Ec, Ev: p.Ev, EFn: p.Efn, EFp: p.Efp,
        }));
        return (
          <div key={s.id} style={{
            background: c.plotBg, border: `1px solid ${c.border}`,
            borderRadius: 6, padding: 10, minHeight: 280,
            display: 'flex', flexDirection: 'column',
          }}>
            <div style={{
              fontFamily: 'IBM Plex Mono, monospace', fontSize: 12,
              color: c.text, marginBottom: 6,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%',
                background: s.color, display: 'inline-block',
              }} />
              {s.name}
            </div>
            {bd.length === 0 ? (
              <div style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: c.textMuted, fontSize: 12,
              }}>No band-diagram data</div>
            ) : (
              <div style={{
                width: '100%', height: 240, minHeight: 240, minWidth: 200,
                position: 'relative',
              }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={bd} margin={{ top: 4, right: 12, bottom: 24, left: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={c.plotGrid} />
                    <XAxis dataKey="x" type="number"
                      tickFormatter={v => Number(v).toFixed(0)}
                      tick={{ fill: c.plotText, fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                      label={{ value: 'x (nm)', position: 'insideBottom', offset: -10,
                        fill: c.plotText, fontSize: 11, fontFamily: 'IBM Plex Mono' }} />
                    <YAxis tick={{ fill: c.plotText, fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                      tickFormatter={v => Number(v).toFixed(2)}
                      width={36} />
                    <Tooltip contentStyle={{ background: c.bgCard, border: `1px solid ${c.border}` }} />
                    <Line type="linear" dataKey="Ec"  stroke={c.accent} strokeWidth={1.6} dot={false} name="Ec" />
                    <Line type="linear" dataKey="Ev"  stroke="#4a9eff" strokeWidth={1.6} dot={false} name="Ev" />
                    <Line type="linear" dataKey="EFn" stroke={c.green} strokeWidth={1.2} dot={false} strokeDasharray="5 3" name="EFn" />
                    <Line type="linear" dataKey="EFp" stroke={c.amber} strokeWidth={1.2} dot={false} strokeDasharray="5 3" name="EFp" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        );
      })}
    </div>
    <PlotExportBar
      chartId="comparison-bands-chart"
      csvData={csvRows}
      csvFilename="comparison_bands"
      plotFilename="comparison_bands"
      c={c}
      disabled={csvRows.length === 0}
    />
    </div>
  );
}

// ── Profiles ────────────────────────────────────────────────────────────────
function ProfilesCompare({ sims, profileKey, setProfileKey, normalized, c }) {
  const field = PROFILE_FIELDS.find(f => f.key === profileKey) || PROFILE_FIELDS[0];

  // Wide-format: per-x row with one column per sim
  const data = useMemo(() => {
    const xs = new Set();
    sims.forEach(s => (s.results?.profiles || []).forEach(p => xs.add(p.x_nm)));
    const sortedX = Array.from(xs).sort((a, b) => a - b);
    const lookup = sims.map(s => {
      const m = new Map();
      const profs = s.results?.profiles || [];
      let max = 0;
      profs.forEach(p => {
        const v = p[field.key];
        if (v != null && Number.isFinite(v)) {
          max = Math.max(max, Math.abs(v));
        }
      });
      profs.forEach(p => {
        const v = p[field.key];
        if (v != null && Number.isFinite(v)) {
          let val = v;
          if (normalized && max > 0) val = v / max;
          if (field.log) {
            val = val > 0 ? Math.log10(val) : null;
          }
          if (val != null) m.set(p.x_nm, val);
        }
      });
      return m;
    });
    return sortedX.map(x => {
      const row = { x };
      sims.forEach((s, i) => {
        const v = lookup[i].get(x);
        if (v != null && Number.isFinite(v)) row[s.name] = v;
      });
      return row;
    });
  }, [sims, normalized, field.key, field.log]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
        <label style={{
          fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, color: c.textMuted,
        }}>Profile:</label>
        <select value={profileKey} onChange={e => setProfileKey(e.target.value)}
          style={{
            background: c.input, color: c.inputText,
            border: `1px solid ${c.border}`, borderRadius: 4,
            padding: '4px 8px', fontFamily: 'IBM Plex Mono, monospace', fontSize: 12,
          }}>
          {PROFILE_FIELDS.map(f => (
            <option key={f.key} value={f.key}>{f.label}</option>
          ))}
        </select>
      </div>
      <div id="comparison-profiles-chart" style={{
        width: '100%', height: 380, minHeight: 380, minWidth: 200,
        position: 'relative',
      }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 30, bottom: 40, left: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={c.plotGrid} />
            <XAxis dataKey="x" type="number"
              tickFormatter={v => Number(v).toFixed(0)}
              tick={{ fill: c.plotText, fontSize: 12, fontFamily: 'IBM Plex Mono' }}
              label={{ value: 'Position (nm)', position: 'insideBottom', offset: -10,
                fill: c.plotText, fontSize: 13, fontFamily: 'IBM Plex Mono' }} />
            <YAxis tick={{ fill: c.plotText, fontSize: 12, fontFamily: 'IBM Plex Mono' }}
              label={{ value: field.log
                ? `log₁₀ ${normalized ? 'normalized' : field.unit}`
                : `${field.label.split('(')[0]}(${field.unit})`,
                angle: -90, position: 'insideLeft', offset: -4,
                textAnchor: 'middle', fill: c.plotText, fontSize: 12,
                fontFamily: 'IBM Plex Mono' }} />
            <Tooltip contentStyle={{ background: c.bgCard, border: `1px solid ${c.border}` }} />
            {sims.map(s => (
              <Line key={s.id} type="monotone" dataKey={s.name}
                stroke={s.color} strokeWidth={2} dot={false} connectNulls />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <PlotExportBar
        chartId="comparison-profiles-chart"
        csvData={data.map(row => {
          const out = { 'position(nm)': row.x };
          sims.forEach(s => {
            out[`${s.name}_${field.key}`] = row[s.name] != null ? row[s.name] : '';
          });
          return out;
        })}
        csvFilename={`comparison_profiles_${field.key}`}
        plotFilename={`comparison_profiles_${field.key}`}
        c={c}
        disabled={data.length === 0}
      />
    </div>
  );
}

// ── EQE ─────────────────────────────────────────────────────────────────────
function EQECompare({
  sims, eqeSpectra, setEqeSpectra,
  eqeRunning, setEqeRunning, eqeProgress, setEqeProgress,
  eqeError, setEqeError, c,
}) {
  const haveAll = sims.every(s => eqeSpectra[s.id]);

  async function runAll() {
    setEqeError(null);
    setEqeRunning(true);
    setEqeProgress({ done: 0, total: sims.length, current: '' });
    const out = { ...eqeSpectra };
    for (let i = 0; i < sims.length; i++) {
      const s = sims[i];
      setEqeProgress({ done: i, total: sims.length, current: s.name });
      const payload = s.results?._lastPayload;
      if (!payload) {
        setEqeError(`Sim "${s.name}" was saved without device payload — re-run and re-save it.`);
        continue;
      }
      try {
        const { data } = await axios.post(`${API}/simulation/eqe`,
          { ...payload, wl_start_nm: 300, wl_end_nm: 1100, wl_step_nm: 25 });
        if (data?.success !== false) {
          out[s.id] = data.spectrum || [];
        } else {
          setEqeError(`${s.name}: ${data.error || 'EQE failed'}`);
        }
      } catch (e) {
        setEqeError(`${s.name}: ${e?.response?.data?.detail || e.message}`);
      }
    }
    setEqeSpectra(out);
    setEqeProgress({ done: sims.length, total: sims.length, current: '' });
    setEqeRunning(false);
  }

  // Wide-format: per-wavelength row, one column per sim
  const data = useMemo(() => {
    const wls = new Set();
    sims.forEach(s => (eqeSpectra[s.id] || []).forEach(p => wls.add(p.wavelength_nm)));
    const sortedW = Array.from(wls).sort((a, b) => a - b);
    const lookup = sims.map(s => {
      const m = new Map();
      (eqeSpectra[s.id] || []).forEach(p => m.set(p.wavelength_nm, p.eqe));
      return m;
    });
    return sortedW.map(w => {
      const row = { wavelength_nm: w };
      sims.forEach((s, i) => {
        const v = lookup[i].get(w);
        if (v != null && Number.isFinite(v)) row[s.name] = v;
      });
      return row;
    });
  }, [sims, eqeSpectra]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button onClick={runAll} disabled={eqeRunning}
          style={{
            padding: '6px 14px', cursor: eqeRunning ? 'not-allowed' : 'pointer',
            opacity: eqeRunning ? 0.6 : 1,
            background: c.btnPrimary, color: c.btnPrimaryText,
            border: 'none', borderRadius: 4,
            fontFamily: 'IBM Plex Mono, monospace', fontSize: 12,
          }}>
          {eqeRunning
            ? `Calculating ${eqeProgress.done}/${eqeProgress.total}: ${eqeProgress.current}…`
            : haveAll
              ? `Recalculate EQE for ${sims.length} sims`
              : `⚡ Calculate EQE for ${sims.length} sims`}
        </button>
        {eqeError && (
          <span style={{
            color: c.red, fontSize: 12,
            fontFamily: 'IBM Plex Mono, monospace',
          }}>{eqeError}</span>
        )}
      </div>
      <div id="comparison-eqe-chart" style={{
        width: '100%', height: 380, minHeight: 380, minWidth: 200,
        position: 'relative',
      }}>
        {data.length === 0 ? (
          <div style={{
            height: '100%', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: c.textMuted, fontSize: 13,
          }}>
            Click "Calculate EQE" — runs ~33 wavelengths per sim, 1–2 min total.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 10, right: 30, left: 20, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={c.plotGrid} />
              <XAxis dataKey="wavelength_nm" type="number" domain={['auto', 'auto']}
                tick={{ fill: c.plotText, fontSize: 12, fontFamily: 'IBM Plex Mono' }}
                label={{ value: 'Wavelength (nm)', position: 'insideBottom', offset: -8,
                  fill: c.plotText, fontSize: 13, fontFamily: 'IBM Plex Mono' }} />
              <YAxis domain={[0, 1]}
                tick={{ fill: c.plotText, fontSize: 12, fontFamily: 'IBM Plex Mono' }}
                label={{ value: 'EQE', angle: -90, position: 'insideLeft', offset: -4,
                  textAnchor: 'middle', fill: c.plotText, fontSize: 13,
                  fontFamily: 'IBM Plex Mono' }} />
              <Tooltip contentStyle={{ background: c.bgCard, border: `1px solid ${c.border}` }} />
              <Legend
                verticalAlign="top"
                align="right"
                wrapperStyle={{ paddingBottom: 0, top: 0 }} />
              {sims.map(s => (
                <Line key={s.id} type="monotone" dataKey={s.name}
                  stroke={s.color} strokeWidth={2} dot={false} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
      <PlotExportBar
        chartId="comparison-eqe-chart"
        csvData={data.map(row => {
          const out = { 'wavelength(nm)': row.wavelength_nm };
          sims.forEach(s => {
            out[`${s.name}_EQE`] = row[s.name] != null ? row[s.name] : '';
          });
          return out;
        })}
        csvFilename="comparison_eqe"
        plotFilename="comparison_eqe"
        c={c}
        disabled={data.length === 0}
      />
    </div>
  );
}
